import React, { useState, useCallback, useEffect } from 'react';
import { Button, Input, Row, Col, Typography, Checkbox, notification, Modal, Spin } from 'antd';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import AgGridTable from '../common/AgGridTable';
import AntdSelectCellRenderer from '../common/AntdSelectCellRenderer';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  fetchTechnicianLog,
  updateTechnicianLog,
  updateDsrLogSections,
} from '../../redux/dsr/action';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Sample Initial Data based on the new UI
const initialData = [
  { id: 1, name: 'Meharana Mohammad', status: 'Mobilize', hrs: 10, overTime: 12 },
  { id: 2, name: 'Christian Booth', status: 'On Site', hrs: 10, overTime: 20 },
];

const TechnicianLogSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  onComplete,
  onCancel,
  isShiftSkipped = false,
  onSkipChange,
  // Redux props
  onFetchTechnicianLog,
  onUpdateTechnicianLog,
  onUpdateDsrLogSections,
  isTechnicianLogLoading,
  isUpdatingTechnicianLog,
  currentTechnicianLog,
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Format the selected date or use today's date as fallback
  const displayDate = selectedDate
    ? moment(selectedDate).format('DD-MMM-YYYY')
    : moment().format('DD-MMM-YYYY');

  // Convert shift string to number for API
  const getShiftNumber = (shiftStr) => {
    return shiftStr === 'day' ? 0 : 1;
  };

  // Convert API data to table format
  const convertApiDataToTableFormat = (apiData) => {
    if (!apiData || !apiData.dsrLogTechnicianMetadata) return [];

    const convertedData = apiData.dsrLogTechnicianMetadata.map((item, index) => ({
      id: item.id,
      name: `${item.userFirstName} ${item.userLastName}`,
      status: getStatusFromApiData(item),
      roles: (typeof item.role === 'string' && item.role.trim().length > 0) ? item.role : 'Select Role',
      hrs: item.hrs || 0,
      overTime: item.overTime || 0,
      // Store original API data for updates
      apiData: item,
    }));

    console.log('Converted API data to table format:', convertedData);
    return convertedData;
  };

  // Map API status or legacy flags to UI label (new allowed set)
  const getStatusFromApiData = (item) => {
    const allowedStatuses = new Set([
      'Mainline',
      'Offline',
      'Offshore',
      'Tie-In',
      'Standby',
      'Mobilize',
      'De-Mobilize',
    ]);

    if (typeof item.status === 'string' && item.status.length > 0) {
      // Normalize to compare case-insensitively and handle minor variants
      const incoming = item.status.trim();
      // If the incoming matches one of the allowed statuses ignoring case, return the canonical casing
      for (const s of allowedStatuses) {
        if (s.toLowerCase() === incoming.toLowerCase()) return s;
      }
    }

    // Legacy flags fallback -> best-effort mapping
    if (item.mobilize === 1) return 'Mobilize';
    if (item.deMobilize === 1) return 'De-Mobilize';

    // If legacy fields explicitly indicate no status
    if (
      item.mobilize === 0 &&
      item.onSite === 0 &&
      item.onLeave === 0 &&
      item.onVisit === 0 &&
      item.onTravel === 0 &&
      item.deMobilize === 0
    ) {
      return 'Select Status';
    }

    return 'Select Status';
  };

  // Convert UI status label to API string format (pass-through of allowed values)
    const mapUiStatusToApi = (status) => {
      const allowed = new Set([
        'Mainline',
        'Offline',
        'Offshore',
        'Tie-In',
        'Standby',
        'Mobilize',
        'De-Mobilize',
      ]);
      if (allowed.has(status)) return status;
      return '';
  };

  // Fetch technician log when component mounts or dependencies change
  useEffect(() => {
    if (selectedProject && selectedMainline && selectedDate && !isSkipped) {
      const userEmail = JSON.parse(localStorage.getItem('user')).email;
      const logDate = moment(selectedDate).format('YYYY-MM-DD');
      const shiftNumber = getShiftNumber(shift);

      onFetchTechnicianLog({
        projectId: selectedProject.id,
        logDate: logDate,
        userEmail: userEmail,
        shift: shiftNumber,
        sowId: selectedMainline.id,
      });
    }
  }, [selectedProject, selectedMainline, selectedDate, shift, isSkipped]);

  // Update local state when API data is received
  useEffect(() => {
    if (currentTechnicianLog) {
      const convertedData = convertApiDataToTableFormat(currentTechnicianLog);
      setRowData(convertedData);
      setComments(currentTechnicianLog.issues || '');
      setIsDirty(false);
    }
  }, [currentTechnicianLog]);

  // Update skip state from props - sync with parent component
  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  // Reset dirty state when date changes (new data should be loaded)
  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  // Reset skip state when section or shift changes
  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  // Get unique roles from currentTechnicianLog
  const getRoles = () => {
    // Prefer roles list from API if provided
    if (Array.isArray(currentTechnicianLog?.roles) && currentTechnicianLog.roles.length > 0) {
      const fromRolesArray = currentTechnicianLog.roles.map(item => item?.role).filter(Boolean);
      const uniqueFromRoles = [...new Set(fromRolesArray)];
      return ['Select Role', ...uniqueFromRoles];
    }

    // Fallback: collect distinct roles from metadata items
    if (Array.isArray(currentTechnicianLog?.dsrLogTechnicianMetadata)) {
      const fromMetadata = currentTechnicianLog.dsrLogTechnicianMetadata
        .map(item => item?.role)
        .filter(role => typeof role === 'string' && role.trim().length > 0);
      const uniqueFromMetadata = [...new Set(fromMetadata)];
      return ['Select Role', ...uniqueFromMetadata];
    }

    return ['Select Role'];
  };

  const columnDefs = [
    { headerName: 'Name', field: 'name', editable: false, flex: 1 },
    {
      headerName: 'Job Title',
      field: 'roles',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: getRoles(),
      },
      flex: 1,
      suppressClickEdit: false,
      singleClickEdit: true,
      cellStyle: (params) => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
          };
        }
        return null;
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: [
          'Select Status',
          'Mainline',
          'Offline',
          'Offshore',
          'Tie-In',
          'Standby',
          'Mobilize',
          'De-Mobilize',
        ],
      },
      flex: 1,
      suppressClickEdit: false,
      singleClickEdit: true,
      cellStyle: (params) => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
          };
        }
        return null;
      },
    },
    
    {
      headerName: 'HRS',
      field: 'hrs',
      type: 'numericColumn',
      editable: true,
      flex: 1,
      suppressClickEdit: false,
      singleClickEdit: true,
      cellStyle: (params) => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
          };
        }
        return null;
      },
    },
    {
      headerName: 'Over Time',
      field: 'overTime',
      type: 'numericColumn',
      editable: true,
      flex: 1,
      suppressClickEdit: false,
      singleClickEdit: true,
      cellStyle: (params) => {
        if (params.colDef.editable && !skipped && !isSubmitted) {
          return {
            backgroundColor: 'rgb(50, 29, 30)',
            borderRadius: '8px',
            border: '1px solid #666',
          };
        }
        return null;
      },
    },
  ];

  const onCellValueChanged = useCallback((params) => {
    // This function will be called when a cell value changes.
    console.log('Cell value changed:', params.data);
    console.log('Field changed:', params.colDef.field, 'New value:', params.newValue);
    setIsDirty(true);

    // Update the local rowData with the new values
    setRowData((prevData) => {
      const updatedData = prevData.map((row) =>
        row.id === params.data.id ? { ...row, ...params.data } : row
      );
      console.log('Updated rowData:', updatedData);
      return updatedData;
    });
  }, []);

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
    setIsDirty(true);
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);

    // Use the parent component's centralized skip handler
    if (onSkipChange) {
      onSkipChange(e);
    } else {
      // Fallback to local handling if no parent handler provided
      if (currentDsrLog?.id) {
        const submissionStatus = generateSubmissionStatus('technician', shift, true);
        const sectionsSkipped = {
          ...currentDsrLog.sectionsSkipped,
          isTechnicianLogSkippedDayShift:
            shift === 'day'
              ? newSkipState
                ? 1
                : 0
              : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedDayShift || 0,
          isTechnicianLogSkippedNightShift:
            shift === 'night'
              ? newSkipState
                ? 1
                : 0
              : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedNightShift || 0,
        };

        onUpdateDsrLogSections({
          dsrLogId: currentDsrLog.id,
          submissionStatus: submissionStatus,
          sectionsSkipped: sectionsSkipped,
        });
      }
    }
  };

  // Helper function to generate submission status fields for a specific section and shift
  const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
    const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: isSubmitted };
  };

  const handleSave = async () => {
    if (skipped) {
      // If skipped, just complete without saving data to API
      if (onComplete) onComplete(true, 'technician');
      return;
    }

    try {
      // Prepare data for API
      const dsrLogTechnicianMetadata = rowData.map((row) => {
        const statusString = mapUiStatusToApi(row.status);
        const roleString = (row.roles && row.roles !== 'Select Role') ? String(row.roles).toLowerCase() : '';
        return {
          id: row.id,
          hrs: row.hrs || 0,
          overTime: row.overTime || 0,
          userEmail: row.apiData?.userEmail,
          userFirstName: row.apiData?.userFirstName,
          userLastName: row.apiData?.userLastName,
          userProjectId: row.apiData?.userProjectId,
          status: statusString,
          role: roleString,
        };
      });

      const payload = {
        dsrLogTechnician: {
          id: currentTechnicianLog?.id,
          dsrLogId: currentDsrLog?.id,
          shift: getShiftNumber(shift),
          issues: comments,
          dsrLogTechnicianMetadata,
        },
      };

      await onUpdateTechnicianLog(payload);
      setIsDirty(false);

      // Update submission status and sectionsSkipped for this section
      const submissionStatus = generateSubmissionStatus('technician', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isTechnicianLogSkippedDayShift:
          shift === 'day' ? 0 : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedDayShift || 0,
        isTechnicianLogSkippedNightShift:
          shift === 'night'
            ? 0
            : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedNightShift || 0,
      };

      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog?.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });

      // Only navigate to next section on successful save
      if (onComplete) onComplete(true, 'technician');

      // Refresh data after successful save
      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = getShiftNumber(shift);

        onFetchTechnicianLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id,
        });
      }
    } catch (error) {
      console.error('Error saving technician log:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save technician log data. Please try again.',
      });
      // Stay in the same section on error - button will be re-enabled
    }
  };

  const handleNext = () => {
    if (isDirty) {
      Modal.confirm({
        title: 'Warning!',
        icon: <ExclamationCircleOutlined />,
        content: 'You have made changes to the table. Do you want to save them?',
        okText: 'Save',
        cancelText: 'Discard',
        onOk: () => {
          handleSave();
          if (onComplete) onComplete(!skipped);
        },
        onCancel: () => {
          if (onComplete) onComplete(!skipped);
        },
      });
    } else {
      if (onComplete) onComplete(!skipped);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {isShiftSkipped ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            backgroundColor: 'rgba(250, 173, 20, 0.1)',
            border: '1px solid #faad14',
            borderRadius: 8,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#faad14',
              marginBottom: 8,
            }}
          >
            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
          </div>
          <div
            style={{
              fontSize: 14,
              color: '#666',
            }}
          >
            This section is disabled because the {shift} shift has been skipped.
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16, height: '40vh', overflow: 'auto' }}>
            <AgGridTable
              rowData={rowData}
              columnDefs={columnDefs.map((col) => ({
                ...col,
                editable: col.editable === false ? false : !skipped && !isSubmitted,
              }))}
              onCellValueChanged={onCellValueChanged}
              theme="ag-theme-material-dark"
              loading={isTechnicianLogLoading}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <Title
              level={5}
              style={{ textAlign: 'left' }}
            >
              Issues
            </Title>
            <TextArea
              rows={3}
              placeholder="Please add your issues here"
              value={comments}
              onChange={handleCommentsChange}
              disabled={skipped || isSubmitted}
            />
          </div>
        </>
      )}

      {/* Fixed Save/Cancel Buttons */}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          display: 'flex',
          gap: 12,
        }}
      >
        <Button
          onClick={onCancel}
          size="large"
          shape="round"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          type="primary"
          size="large"
          shape="round"
          disabled={isUpdatingTechnicianLog || isSubmitted || isShiftSkipped}
          loading={isUpdatingTechnicianLog}
        >
          Save & Next
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isTechnicianLogLoading: dsrReducer.isTechnicianLogLoading,
  isUpdatingTechnicianLog: dsrReducer.isUpdatingTechnicianLog,
  currentTechnicianLog: dsrReducer.currentTechnicianLog,
});

const mapDispatchToProps = (dispatch) => ({
  onFetchTechnicianLog: (payload) => dispatch(fetchTechnicianLog(payload)),
  onUpdateTechnicianLog: (payload) => dispatch(updateTechnicianLog(payload)),
  onUpdateDsrLogSections: (payload) => dispatch(updateDsrLogSections(payload)),
});

AntdSelectCellRenderer.displayName = 'AntdSelectCellRenderer';
export default connect(mapStateToProps, mapDispatchToProps)(TechnicianLogSection);